import React from 'react';
import { Button, MessageBar, Spinner } from '@fluentui/react-components';
import { useConfirmStorefrontLoginMutation, useGetBasicStorefrontQuery } from '../Api/Api';
import { SK_ORGANIZATION, SK_STOREFRONT } from '../Constants/LocalStorageKeys';
import { useLocalizationUnauthenticated } from '../Components/Helpers/useLocalization';
import LoginPageShell from '../Components/Auth/LoginPageShell';

// Where the emailed link lands when the storefront's login method is 'poll'.
//
// This tab's job is to RELEASE the session to the tab that is waiting, not to sign in here — the
// whole point of the polling flow is that the shopper keeps the page they were already on, with
// their cart and their place in it. So this page confirms and then tells them to go back.
//
// The token still rides in the URL as a fallback: if the waiting tab has been closed, or the pending
// login expired before they got to their inbox, "continue here" signs them in on this device instead
// of stranding them. That is the same token today's magic link already puts in the email, so it is
// no new exposure.
const StorefrontLoginConfirmPage: React.FC = () => {
    const L = useLocalizationUnauthenticated();
    const params = new URLSearchParams(window.location.search);
    const pendingId = params.get('p') ?? '';
    const token = params.get('token') ?? '';
    const redirect = params.get('redirect') ?? '';

    // From the link first: it is emailed, so it is routinely opened on a phone or in another browser
    // where this storage does not exist, and reading only from storage made a perfectly good link
    // report itself as expired. Storage stays as the fallback for links sent before the BFF started
    // including them.
    const organization = params.get('o') || localStorage.getItem(SK_ORGANIZATION) || '';
    const storefront = params.get('s') || localStorage.getItem(SK_STOREFRONT) || '';

    // Styling only, so the tab the emailed link opens looks like the storefront it belongs to.
    const sSettings = useGetBasicStorefrontQuery({ organization, storefront }, { skip: !organization || !storefront });
    const [confirm] = useConfirmStorefrontLoginMutation();
    const [state, setState] = React.useState<'working' | 'confirmed' | 'expired'>('working');
    const ran = React.useRef(false);

    React.useEffect(() => {
        // Once only: a re-render must not re-confirm (harmless, but it would also re-issue the request
        // after the record has been consumed by the waiting tab).
        if (ran.current) return;
        ran.current = true;
        if (!pendingId || !organization || !storefront) { setState('expired'); return; }
        confirm({ organization, storefront, pendingId })
            .unwrap()
            .then((r) => setState(r.status === 'confirmed' ? 'confirmed' : 'expired'))
            .catch(() => setState('expired'));
    }, [confirm, pendingId, organization, storefront]);

    const continueHere = () => {
        const to = redirect ? `&redirect=${encodeURIComponent(redirect)}` : '';
        window.location.assign(`/token?token=${encodeURIComponent(token)}${to}`);
    };

    return (
        <LoginPageShell config={sSettings.data?.loginPageConfig} storefrontName={sSettings.data?.name} favicon={sSettings.data?.favicon}>
            <>
                {state === 'working' && <Spinner label={L('localization_common_loading')} />}

                {state === 'confirmed' && (
                    <>
                        <h2>{L('localization_auth_confirm_title', "You're signed in")}</h2>
                        <p>{L('localization_auth_confirm_body', 'Head back to the tab where you started and carry on. You can close this one.')}</p>
                    </>
                )}

                {state === 'expired' && (
                    <>
                        <MessageBar intent='warning'>
                            {L('localization_auth_confirm_expired', 'This sign-in request has expired or was already used.')}
                        </MessageBar>
                    </>
                )}

                {/* Offered in both end states: after confirming, for someone who read the email on a
                    different device and would rather just continue here. */}
                {state !== 'working' && !!token && (
                    <Button appearance='primary' style={{ width: '100%', marginTop: '1em' }} onClick={continueHere}>
                        {L('localization_auth_confirm_continue_here', 'Continue on this device')}
                    </Button>
                )}
            </>
        </LoginPageShell>
    );
};

export default StorefrontLoginConfirmPage;
